import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["aria-hidden", "aria-label", "role", "innerHTML"]

import { computed } from 'vue';
import type { CSSProperties, PropType } from 'vue';
import icons from './icons';

type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];

type SizeClass = (typeof SIZE_CLASS_VALUES)[number];

interface FullDimensionsRem {
  widthRem: number;
  heightRem: number;
}

type DimensionsRem = AtLeastOne<FullDimensionsRem>;
type IconSize = DimensionsRem | SizeClass;

const WC_ICON_ASPECT_RATIO_VAR = '--wc-icon-aspect-ratio' as const;


export default /*@__PURE__*/_defineComponent({
  __name: 'WCSvgIcon',
  props: {
  name: {
    type: String,
    required: true,
  },
  ariaLabel: {
    type: String,
    default: null,
  },
  iconSize: {
    type: [String, Object] as PropType<IconSize>,
    default: null,
  },
  aspectRatio: {
    type: String,
    default: null,
  },
},
  setup(__props) {

const SIZE_CLASS_VALUES = ['3xs', '2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2x', '3x'] as const;
const ICON_SIZE_MAP: Record<SizeClass, string> = {
  '3xs': '0.625rem',
  '2xs': '0.6875rem',
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.25rem',
  xl: '1.5rem',
  '2x': '2rem',
  '3x': '3rem',
};

const props = __props;

const toRem = (value?: number): string | null => (typeof value === 'number' ? `${value}rem` : null);

const hasAriaLabel = computed(() => !!props.ariaLabel?.trim());
const computedRole = computed(() => (hasAriaLabel.value ? 'img' : undefined));
const computedAriaLabel = computed(() => (hasAriaLabel.value ? props.ariaLabel : undefined));
const ariaHidden = computed(() => (hasAriaLabel.value ? undefined : 'true'));

const hasCustomDimensions = computed(() => !!(props.iconSize || props.aspectRatio));

const iconStyle = computed(() => {
  const style: CSSProperties = {};

  if (typeof props.iconSize === 'string') {
    const resolvedSize = ICON_SIZE_MAP[props.iconSize as SizeClass];

    if (resolvedSize) {
      style.width = resolvedSize;
      style.height = resolvedSize;
    }
  } else if (props.iconSize && typeof props.iconSize === 'object') {
    const width = toRem(props.iconSize.widthRem);
    const height = toRem(props.iconSize.heightRem);

    if (width || height) {
      style.width = width || 'auto';
      style.height = height || 'auto';
    }
  }

  if (props.aspectRatio) {
    style[WC_ICON_ASPECT_RATIO_VAR] = props.aspectRatio;
  }

  if (!Object.keys(style).length) {
    return undefined;
  }

  return style;
});

const svgMarkup = computed(() => {
  const svg = icons[props.name];

  if (!svg) {
    console.warn(`[WCSvgIcon] Icon "${props.name}" not found in icons.json`);
    return '';
  }

  return svg;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("span", {
    class: _normalizeClass(["wc-icon", { 'wc-icon--custom-size': hasCustomDimensions.value }]),
    style: _normalizeStyle(iconStyle.value),
    "aria-hidden": ariaHidden.value,
    "aria-label": computedAriaLabel.value,
    role: computedRole.value,
    innerHTML: svgMarkup.value
  }, null, 14, _hoisted_1))
}
}

})